import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import {
    MdCheck,
    MdClose,
    MdLockOutline,
    MdOutlineReceiptLong,
    MdOutlineGroups,
    MdOutlineAutorenew,
    MdOutlineWorkspacePremium,
} from "react-icons/md";
import { BillingType, CurrentSubscription, Plan, PricingData } from "../../types/pricingType";
import { AppDispatch } from "../../store";
import {
    fetchPricing,
    createSubscriptionCheckout,
    verifySubscriptionPayment,
} from "../../features/pricing/PricingService";
import customFetch from "../../util/config/axiosConfig";
import { getAuthUser } from "../../util/manageAuth";
import { loadRazorpayScript } from "../../util/razorpay";

const PERIOD: Record<string, string> = {
    monthly: "month",
    quarterly: "3 months",
    half_yearly: "6 months",
    annually: "year",
};

const inr = (v: number) => `₹${Math.round(v).toLocaleString("en-IN")}`;

const dateOf = (ms?: number | null) =>
    ms ? new Date(ms).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";

export default function PricingCards() {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const authUser = getAuthUser();

    const [billingType, setBillingType] = useState<BillingType>("annually");
    const [pricing, setPricing] = useState<PricingData | null>(null);
    const [current, setCurrent] = useState<CurrentSubscription | null>(null);
    const [loading, setLoading] = useState(true);
    const [processingPlan, setProcessingPlan] = useState<string | null>(null);

    const loadPricing = useCallback(async () => {
        try {
            setLoading(true);
            const [result, cur] = await Promise.all([
                dispatch(fetchPricing()).unwrap(),
                customFetch
                    .get("/api/subscriptions/current")
                    .then((r) => (r.data?.data ?? null) as CurrentSubscription | null)
                    .catch(() => null),
            ]);
            setPricing(result);
            setCurrent(cur);
            // start on a period the plans are actually sold for
            const ids = (result?.billingOptions || []).map((o: { id: BillingType }) => o.id);
            setBillingType((b) => (ids.length && !ids.includes(b) ? ids[ids.length - 1] : b));
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        loadPricing();
    }, [loadPricing]);

    /** The plan you're on now (for this billing period). */
    const isCurrent = (plan: Plan) =>
        !!current && current.planKey === plan.planKey && (current.billingType === billingType || plan.isEnterprise);

    const handleGetStarted = async (plan: Plan) => {
        if (plan.isEnterprise) {
            navigate("/contact");
            return;
        }
        if (isCurrent(plan) || plan.disabled || processingPlan) return;

        setProcessingPlan(plan.id);

        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
            toast.error("Failed to load payment gateway. Check your connection.");
            setProcessingPlan(null);
            return;
        }

        try {
            // STEP 1 — create order on backend
            const order = await dispatch(
                createSubscriptionCheckout({
                    planKey: plan.planKey,
                    billingType,
                })
            ).unwrap();

            // Backend already activated the plan (nothing to pay)
            if (order.activated) {
                toast.success(`${plan.title} plan activated!`);
                await loadPricing();
                setProcessingPlan(null);
                return;
            }

            // STEP 2 — open Razorpay modal
            const rzp = new window.Razorpay({
                key: order.razorpayKey || process.env.REACT_APP_RAZORPAY_KEY || "",
                amount: order.amount, // in paise from backend
                currency: order.currency,
                name: order.name || "Subscription",
                description:
                    order.description ||
                    `${plan.title} plan (${billingType === "annually" ? "Yearly" : "Monthly"})`,
                order_id: order.orderId,

                // STEP 3 — verify payment on success
                handler: async (response: {
                    razorpay_order_id: string;
                    razorpay_payment_id: string;
                    razorpay_signature: string;
                }) => {
                    try {
                        await dispatch(
                            verifySubscriptionPayment({
                                razorpayOrderId: response.razorpay_order_id,
                                razorpayPaymentId: response.razorpay_payment_id,
                                razorpaySignature: response.razorpay_signature,
                            })
                        ).unwrap();

                        toast.success(`${plan.title} plan activated!`);
                        await loadPricing();
                    } catch (err: any) {
                        toast.error(
                            typeof err === "string"
                                ? err
                                : "Payment received but verification failed. Contact support."
                        );
                    } finally {
                        setProcessingPlan(null);
                    }
                },

                modal: {
                    ondismiss: () => {
                        toast.info("Payment cancelled");
                        setProcessingPlan(null);
                    },
                },

                prefill: {
                    name: order.prefillName || authUser?.userName || "",
                    // backend may send a non-email value (e.g. "admin"), so validate
                    email: [authUser?.email, order.prefillEmail].find((e) => !!e && e.includes("@")) || "",
                    contact: order.prefillContact || authUser?.mobile || "",
                },

                theme: { color: "#0288D1" },
            });

            rzp.on("payment.failed", (response: any) => {
                toast.error(response?.error?.description || "Payment failed. Please try again.");
                setProcessingPlan(null);
            });

            rzp.open();
        } catch (err: any) {
            toast.error(typeof err === "string" ? err : err?.message || "Failed to start checkout");
            setProcessingPlan(null);
        }
    };

    const plans = pricing?.plans || [];
    const cols =
        plans.length >= 4 ? "lg:grid-cols-4" : plans.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2 max-w-4xl";

    return (
        <div className="flex flex-col items-center gap-8 w-full">
            {/* Heading */}
            <div className="text-center max-w-2xl">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#e8f4fb] px-3 py-1 text-xs font-semibold text-brand-main">
                    <MdOutlineWorkspacePremium size={14} /> Plans for every stage
                </span>
                <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                    Grow your business on WhatsApp
                </h1>
                <p className="mt-2 text-sm sm:text-base text-slate-500">
                    Pick a plan for your team. Messages are charged from your wallet at WhatsApp's rates.
                </p>
            </div>

            {/* Current plan */}
            {current && (
                <div className="w-full max-w-3xl flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/70 px-5 py-3.5">
                    <div className="flex items-center gap-3">
                        <span className="grid h-9 w-9 place-items-center rounded-full bg-emerald-500 text-white">
                            <MdCheck size={18} />
                        </span>
                        <div>
                            <p className="text-sm font-semibold text-emerald-900">
                                You're on the {current.planTitle} plan
                                {current.billingType
                                    ? ` · ${
                                          pricing?.billingOptions.find((o) => o.id === current.billingType)?.label ||
                                          current.billingType.replace("_", "-")
                                      }`
                                    : ""}
                            </p>
                            {current.endAt && (
                                <p className="text-xs text-emerald-700">
                                    Runs until {dateOf(current.endAt)}
                                    {current.daysLeft != null ? ` (${current.daysLeft} days left)` : ""}
                                </p>
                            )}
                        </div>
                    </div>
                    <span className="text-xs text-emerald-700">Changing plan credits your unused days.</span>
                </div>
            )}

            {/* Billing period */}
            {pricing && pricing.billingOptions.length > 1 && (
                <div className="inline-flex rounded-full bg-slate-100 p-1 shadow-inner" role="tablist" aria-label="Billing period">
                    {pricing.billingOptions.map((option) => {
                        const on = billingType === option.id;
                        return (
                            <button
                                key={option.id}
                                role="tab"
                                aria-selected={on}
                                onClick={() => setBillingType(option.id)}
                                className={`relative flex items-center gap-2 rounded-full px-4 sm:px-5 py-2 text-sm font-semibold transition-all ${
                                    on ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-800"
                                }`}
                            >
                                {option.label}
                                {option.discount && (
                                    <span
                                        className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                                            on ? "bg-emerald-100 text-emerald-700" : "bg-emerald-50 text-emerald-600"
                                        }`}
                                    >
                                        {option.discount}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Plans */}
            {loading && !pricing ? (
                <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full`}>
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="h-[460px] animate-pulse rounded-3xl bg-slate-100" />
                    ))}
                </div>
            ) : (
                <div className={`grid grid-cols-1 sm:grid-cols-2 ${cols} gap-6 w-full items-stretch mx-auto`}>
                    {plans.map((plan) => (
                        <PlanCard
                            key={plan.id}
                            plan={plan}
                            billingType={billingType}
                            current={isCurrent(plan)}
                            busy={processingPlan === plan.id}
                            locked={!!processingPlan}
                            onChoose={() => handleGetStarted(plan)}
                        />
                    ))}
                </div>
            )}

            {/* Trust strip */}
            <div className="grid w-full max-w-4xl grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                <Trust icon={<MdLockOutline size={18} />} title="Secure checkout" text="Payments by Razorpay" />
                <Trust icon={<MdOutlineReceiptLong size={18} />} title="Clear pricing" text="GST shown before you pay" />
                <Trust icon={<MdOutlineAutorenew size={18} />} title="Switch any time" text="Unused days are credited" />
            </div>

            <div className="flex flex-col items-center gap-1 text-center">
                {pricing?.taxNote && <p className="text-xs text-slate-500">* {pricing.taxNote}</p>}
                {pricing?.messageRatesNote && (
                    <p className="text-xs text-slate-400 max-w-2xl">{pricing.messageRatesNote}</p>
                )}
            </div>
        </div>
    );
}

interface PlanCardProps {
    plan: Plan;
    billingType: BillingType;
    current: boolean;
    busy: boolean;
    locked: boolean;
    onChoose: () => void;
}

function PlanCard({ plan, billingType, current, busy, locked, onChoose }: PlanCardProps) {
    const info = plan.prices?.find((p) => p.billingType === billingType);
    const total = plan.price?.[billingType] ?? info?.totalPrice ?? 0;
    const months = info?.months || (billingType === "annually" ? 12 : billingType === "half_yearly" ? 6 : 1);
    const perMonth = info?.perMonth ?? (months ? total / months : total);
    const free = !plan.isEnterprise && total <= 0;
    const soldForPeriod = plan.isEnterprise || free || total > 0;
    const popular = plan.isPopular && !plan.isEnterprise;
    const dark = plan.isEnterprise;
    const features =
        plan.featureList && plan.featureList.length
            ? plan.featureList
            : (plan.features || []).map((text) => ({ text, included: true }));
    const seats = plan.includedAgents ?? 0;

    const cta = current
        ? "Current plan"
        : busy
          ? "Opening checkout…"
          : plan.isEnterprise
            ? "Contact sales"
            : plan.buttonText || "Choose plan";

    return (
        <div
            className={`relative flex flex-col rounded-3xl p-6 sm:p-7 transition-all duration-200 ${
                dark
                    ? "bg-[#0b1220] text-white shadow-xl"
                    : popular
                      ? "bg-white shadow-[0_20px_50px_-20px_rgba(2,124,190,0.45)] ring-2 ring-brand-main lg:-translate-y-2"
                      : "bg-white border border-slate-200 shadow-sm hover:shadow-lg hover:-translate-y-0.5"
            } ${current ? "ring-2 ring-emerald-400" : ""}`}
        >
            {popular && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand-blue-gradient px-4 py-1 text-xs font-bold text-white shadow">
                    Most popular
                </span>
            )}
            {current && (
                <span className="absolute top-5 right-5 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Your plan
                </span>
            )}

            <h2 className={`text-lg font-bold ${dark ? "text-white" : "text-slate-900"}`}>{plan.title}</h2>
            {plan.description && (
                <p className={`mt-1 text-sm min-h-[40px] ${dark ? "text-slate-300" : "text-slate-500"}`}>{plan.description}</p>
            )}

            {/* Price */}
            <div className="mt-5 min-h-[92px]">
                {plan.isEnterprise ? (
                    <>
                        <p className="text-4xl font-bold tracking-tight">Custom</p>
                        <p className="mt-2 text-sm text-slate-300">Volume pricing, onboarding and a dedicated manager.</p>
                    </>
                ) : free ? (
                    <>
                        <p className="text-4xl font-bold tracking-tight text-slate-900">Free</p>
                        <p className="mt-2 text-sm text-slate-500">No card needed</p>
                    </>
                ) : (
                    <>
                        <p className="flex items-baseline gap-1">
                            <span className="text-4xl font-bold tracking-tight text-slate-900">{inr(perMonth)}</span>
                            <span className="text-sm font-medium text-slate-500">/month</span>
                        </p>
                        <p className="mt-2 text-xs text-slate-500">
                            {months > 1 ? `${inr(total)} billed every ${PERIOD[billingType] || `${months} months`}` : "Billed monthly"}
                            {info ? ` · + ${inr(info.gstAmount)} GST` : ""}
                        </p>
                        {info && info.savingsPercent > 0 && (
                            <span className="mt-2 inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700">
                                Save {Math.round(info.savingsPercent)}%
                            </span>
                        )}
                    </>
                )}
            </div>

            {/* Team seats */}
            {!plan.isEnterprise && (
                <div className="mt-4 flex items-start gap-2.5 rounded-2xl bg-slate-50 px-3.5 py-3">
                    <MdOutlineGroups className="mt-0.5 flex-none text-brand-main" size={18} />
                    <div className="text-sm">
                        <p className="font-semibold text-slate-800">
                            Owner{seats > 0 ? ` + ${seats} team member${seats === 1 ? "" : "s"}` : " only"}
                        </p>
                        {plan.extraAgentPrice ? (
                            <p className="text-xs text-slate-500">Extra seats {inr(plan.extraAgentPrice)}/month each</p>
                        ) : null}
                    </div>
                </div>
            )}

            {/* CTA */}
            <button
                onClick={onChoose}
                disabled={current || plan.disabled || (locked && !busy) || !soldForPeriod}
                className={`mt-6 w-full rounded-full py-3 text-sm font-bold transition-all disabled:cursor-not-allowed ${
                    current
                        ? "bg-emerald-50 text-emerald-700"
                        : dark
                          ? "bg-white text-slate-900 hover:bg-slate-100"
                          : popular
                            ? "bg-brand-blue-gradient text-white shadow-md hover:shadow-lg hover:brightness-110"
                            : "bg-slate-900 text-white hover:bg-slate-800"
                } ${!current ? "disabled:opacity-50" : ""}`}
            >
                {cta}
            </button>

            {/* Features */}
            {features.length > 0 && (
                <>
                    <p className={`mt-6 text-xs font-bold uppercase tracking-wider ${dark ? "text-slate-400" : "text-slate-400"}`}>
                        What's included
                    </p>
                    <ul className="mt-3 space-y-2.5">
                        {features.map((f, i) => (
                            <li key={i} className="flex items-start gap-2.5 text-sm">
                                <span
                                    className={`mt-0.5 grid h-[18px] w-[18px] flex-none place-items-center rounded-full ${
                                        f.included
                                            ? dark
                                                ? "bg-white/15 text-white"
                                                : "bg-[#e8f4fb] text-brand-main"
                                            : dark
                                              ? "bg-white/5 text-slate-500"
                                              : "bg-slate-100 text-slate-400"
                                    }`}
                                >
                                    {f.included ? <MdCheck size={12} /> : <MdClose size={12} />}
                                </span>
                                <span
                                    className={
                                        f.included
                                            ? dark
                                                ? "text-slate-100"
                                                : "text-slate-700"
                                            : `line-through ${dark ? "text-slate-500" : "text-slate-400"}`
                                    }
                                >
                                    {f.text}
                                </span>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}

function Trust({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
    return (
        <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3">
            <span className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-[#e8f4fb] text-brand-main">{icon}</span>
            <div>
                <p className="text-sm font-semibold text-slate-800">{title}</p>
                <p className="text-xs text-slate-500">{text}</p>
            </div>
        </div>
    );
}
